/**
 * 曲线采样点。
 */
export interface CurveSampleSegmentPointLike {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/**
 * 曲线采样段数配置。
 */
export interface CurveSampleSegmentOptions {
    /** 最小采样段数。 */
    minSegments?: number;
    /** 最大采样段数。 */
    maxSegments?: number;
    /** 每个控制点至少分配的采样段数。 */
    segmentsPerControlPoint?: number;
    /** 包围盒对角线的目标分段数。 */
    diagonalDivisions?: number;
}

/** 默认最小采样段数，面向普通交互路径，避免所有曲线操作被闭合样条拉伸拖慢。 */
export const DEFAULT_CURVE_SAMPLE_MIN_SEGMENTS = 64;
/** 默认最大采样段数，限制鼠标移动和普通草图重建阶段的采样点规模。 */
export const DEFAULT_CURVE_SAMPLE_MAX_SEGMENTS = 192;
/** 默认每个控制点的采样段数。 */
const DEFAULT_SEGMENTS_PER_CONTROL_POINT = 16;
/** 默认包围盒对角线分段数。 */
const DEFAULT_DIAGONAL_DIVISIONS = 96;
/**
 * 实时草图预览采样策略。
 *
 * 说明：鼠标移动阶段必须优先保证交互响应，最终草图对象和特征生成会使用更高一档策略重算。
 */
export const SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS: CurveSampleSegmentOptions = {
    minSegments: 32,
    maxSegments: 96,
    segmentsPerControlPoint: 8,
    diagonalDivisions: 64,
};

/**
 * 草图对象显示采样策略。
 *
 * 说明：该策略对应普通草图显示和编辑重建，曲线放大后允许看出分段，行为更接近 SolidWorks 图像质量中档。
 */
export const SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS: CurveSampleSegmentOptions = {
    minSegments: DEFAULT_CURVE_SAMPLE_MIN_SEGMENTS,
    maxSegments: DEFAULT_CURVE_SAMPLE_MAX_SEGMENTS,
    segmentsPerControlPoint: DEFAULT_SEGMENTS_PER_CONTROL_POINT,
    diagonalDivisions: DEFAULT_DIAGONAL_DIVISIONS,
};

/**
 * 曲线 profile 特征采样策略。
 *
 * 说明：特征 profile 需要比实时预览稳定，但不能把封闭曲线无限细分，否则相交布尔会被曲率复杂度拖慢。
 */
export const CURVE_PROFILE_CURVE_SAMPLE_OPTIONS: CurveSampleSegmentOptions = {
    minSegments: 96,
    maxSegments: 384,
    segmentsPerControlPoint: 16,
    diagonalDivisions: 160,
};
/**
 * 闭合样条草图显示采样配置。
 *
 * 说明：
 * 1. 该配置服务草图对象重建与绘制收尾，必须保持中档，避免样式样条第三点后卡顿；
 * 2. 曲线 profile 特征的高精度采样必须走 `CURVE_PROFILE_CURVE_SAMPLE_OPTIONS`，不能污染草图显示层。
 */
export const CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS: CurveSampleSegmentOptions = {
    minSegments: DEFAULT_CURVE_SAMPLE_MIN_SEGMENTS,
    maxSegments: DEFAULT_CURVE_SAMPLE_MAX_SEGMENTS,
    segmentsPerControlPoint: DEFAULT_SEGMENTS_PER_CONTROL_POINT,
    diagonalDivisions: DEFAULT_DIAGONAL_DIVISIONS,
};
/** 零长度判定容差。 */
const CURVE_SAMPLE_EPSILON = 1e-9;

/**
 * 计算三维点距离。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 两点距离。
 */
function computePointDistance(
    left: CurveSampleSegmentPointLike,
    right: CurveSampleSegmentPointLike,
): number {
    return Math.hypot(left.x - right.x, left.y - right.y, left.z - right.z);
}

/**
 * 计算控制折线总长度。
 *
 * @param points 控制点。
 * @returns 控制折线长度。
 */
function computeControlPolygonLength(points: readonly CurveSampleSegmentPointLike[]): number {
    let length = 0;
    for (let index = 1; index < points.length; index += 1) {
        length += computePointDistance(points[index - 1]!, points[index]!);
    }
    return length;
}

/**
 * 计算包围盒对角线长度。
 *
 * @param points 控制点。
 * @returns 包围盒对角线长度。
 */
function computeBoundingDiagonal(points: readonly CurveSampleSegmentPointLike[]): number {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;

    points.forEach((point) => {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        minZ = Math.min(minZ, point.z);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
        maxZ = Math.max(maxZ, point.z);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
        return 0;
    }
    return Math.hypot(maxX - minX, maxY - minY, maxZ - minZ);
}

/**
 * 限制采样段数范围。
 *
 * @param value 原始段数。
 * @param minSegments 最小段数。
 * @param maxSegments 最大段数。
 * @returns 合法段数。
 */
function clampSegmentCount(value: number, minSegments: number, maxSegments: number): number {
    return Math.min(maxSegments, Math.max(minSegments, Math.ceil(value)));
}

/**
 * 根据曲线控制点复杂度解析采样段数。
 *
 * 设计说明：
 * 1. 固定 64 段会让大跨度、不规则闭合样条在草图阶段就呈现多段线；
 * 2. 控制折线长度能反映曲线绕行复杂度，包围盒对角线用于把不同模型尺寸归一化；
 * 3. 采样段数只影响显示和区域解析密度，不改变 Puppy CAD 内核 曲线建模的控制点语义。
 *
 * @param points 控制点或插值点。
 * @param options 采样配置。
 * @returns 建议采样段数。
 */
export function resolveCurveSampleSegments(
    points: readonly CurveSampleSegmentPointLike[],
    options: CurveSampleSegmentOptions = {},
): number {
    const minSegments = Math.max(1, Math.floor(options.minSegments ?? DEFAULT_CURVE_SAMPLE_MIN_SEGMENTS));
    const maxSegments = Math.max(minSegments, Math.floor(options.maxSegments ?? DEFAULT_CURVE_SAMPLE_MAX_SEGMENTS));
    if (points.length < 2) {
        return minSegments;
    }

    const segmentsPerControlPoint = Math.max(
        1,
        Math.floor(options.segmentsPerControlPoint ?? DEFAULT_SEGMENTS_PER_CONTROL_POINT),
    );
    const diagonalDivisions = Math.max(1, Math.floor(options.diagonalDivisions ?? DEFAULT_DIAGONAL_DIVISIONS));
    const controlPointSegments = points.length * segmentsPerControlPoint;
    const polygonLength = computeControlPolygonLength(points);
    const diagonal = computeBoundingDiagonal(points);
    const normalizedTargetLength = diagonal > CURVE_SAMPLE_EPSILON
        ? diagonal / diagonalDivisions
        : 1;
    const lengthSegments = polygonLength > CURVE_SAMPLE_EPSILON
        ? polygonLength / Math.max(normalizedTargetLength, CURVE_SAMPLE_EPSILON)
        : minSegments;

    return clampSegmentCount(
        Math.max(controlPointSegments, lengthSegments),
        minSegments,
        maxSegments,
    );
}
